import { useVisitPricingSummaries, useWorkareaPricingPreviews, useWorkareaPricingInvalidator } from '../../domain/workareas/workareaPricingQueries'
import { mergeServerPricingOntoWorkareas, applyWorkareaPricingPreviews, buildPricingPreviewRequests } from './workareaPricingModel'
import React, { createContext, useEffect, useReducer, useState } from 'react'
import { Dispatch, ChangeSet, createDefaultState, reducer, ItemWorkareaData, ItemVisitData, buildUpdateJobItemsMutationData } from './itemModel'
import { UseQueryResult } from 'react-query'
import { useJobItems, useJobItemsInvalidator, useJobItemsMutation } from './itemQueries'
import { YearlyBudget } from '../../domain/budget'
import { GetJobWorkareaItemsResponse } from './itemsApi'
import { VisitSummaryItem, WorkareaSummary, buildVisitSummary, buildWorkareaSummaries } from './itemSummaryModel'
import { getRecurringDispatchIfNeeded } from './recurringWorkareaModel'
import CatalogItemHandler from './CatalogItemHandler'
import { CatalogTemplateWithItems } from '../../domain/catalogTemplate'
import { CatalogItem } from '../../domain/catalogItem'
import { useWorkareaGroupsInvalidator, useWorkareaGroupsQuery } from '../../domain/workareas/groupsQueries'
import { PricingTypes, ProjectType, ProjectTypes } from '../../common/constants'
import { useCompletedWorkareaVisitsQuery } from '../../domain/invoicing/invoicingQueries'
import { useDebouncedCallback } from '../../helpers/debounce'
import { useInvalidateVisits, useVisitsQuery } from '../../domain/jobs/jobsQueries'
import { Alert, Button, notification } from 'antd'

export type FocusedView = 'workarea-estimated' | 'workarea-actual' | 'visits' | 'costing-and-analysis' | 'receipt' | 'invoicing'

export type OnTemplateAdd = (workareaId: number, template: CatalogTemplateWithItems) => void

export type JobData = {
  jobId: number
  jobType: ProjectType
  jobIsSold: boolean
  workareas: ItemWorkareaData[]
  budgets: YearlyBudget[]
  visitData: ItemVisitData[]
}

export type ItemsContextData = {
  dispatch: Dispatch
  changeSet: ChangeSet
  jobData: JobData
  focusedView: FocusedView
  focusedWorkareaId?: number
  focusedReceiptId?: number
  jobItemsQuery: UseQueryResult<GetJobWorkareaItemsResponse, unknown>
  lastAddedCatalogItem?: LastAddedCatalogItemWithName
  workareaSummaries: WorkareaSummary[]
  autoSave: boolean
  isPricingPending?: boolean
  isPricingError?: boolean
  visitSummary: VisitSummaryItem[]
}

export const ItemsContext = createContext<ItemsContextData>({
  dispatch: () => { },
  changeSet: createDefaultState(),
  jobData: {
    jobId: 0,
    jobType: ProjectTypes.Project,
    jobIsSold: false,
    workareas: [],
    budgets: [],
    visitData: [],
  },
  focusedView: 'workarea-estimated',
  jobItemsQuery: null!,
  workareaSummaries: [],
  autoSave: false,
  visitSummary: []
})

type Props = React.PropsWithChildren & {
  jobId: number
  jobType: ProjectType
  jobIsSold: boolean
  onChange: (changeSet: ChangeSet, visitSummary: VisitSummaryItem[], workareaSummaries: WorkareaSummary[]) => void
  saveCounter: number
  workareas: ItemWorkareaData[]
  budgets: YearlyBudget[]
  focusedView: FocusedView
  focusedWorkareaId?: number
  focusedReceiptId?: number
  onTemplateAdd: OnTemplateAdd
  copyChangesToChildWorkareas?: boolean
  triggerSummariesCounter?: number
  autoSave?: boolean
}

export type LastAddedCatalogItemWithName = {
  catalogItem: CatalogItem
  name?: string
}

const ItemsRoot = ({
  children,
  jobId,
  jobType,
  jobIsSold,
  onChange,
  saveCounter,
  workareas,
  budgets,
  focusedView,
  focusedWorkareaId,
  focusedReceiptId,
  onTemplateAdd,
  copyChangesToChildWorkareas,
  triggerSummariesCounter,
  autoSave,
}: Props) => {
  const [notificationApi, notificationContext] = notification.useNotification()

  const visitsQuery = useVisitsQuery(jobId)
  const groupsQuery = useWorkareaGroupsQuery(jobId)
  const jobData = {
    jobId,
    jobType,
    jobIsSold,
    workareas: mergeServerPricingOntoWorkareas(workareas ?? [], groupsQuery.data ?? []),
    budgets: budgets ?? [],
    visitData: visitsQuery.data ?? []
  }

  const [workareaSummaries, setWorkareaSummaries] = useState<WorkareaSummary[]>([])
  const [visitSummary, setVisitSummary] = useState<VisitSummaryItem[]>([])

  const completedWorkareaVisitsQuery = useCompletedWorkareaVisitsQuery(jobId, jobData.workareas.some(x => x.pricingType === PricingTypes.PerVisit))

  const jobItemsQuery = useJobItems(jobId)

  const jobItemsMutation = useJobItemsMutation()

  const handleDebounce = useDebouncedCallback((jobId: number, autoSaveChanges: ChangeSet, serverData: GetJobWorkareaItemsResponse) => {
    const request = buildUpdateJobItemsMutationData(jobId, autoSaveChanges, serverData)
    jobItemsMutation.mutate(request)
    dispatch({ type: 'CLEAR_CHANGE_SET' })
  }, 2500)

  const [changeSet, dispatch] = useReducer(reducer, createDefaultState())

  useEffect(() => {
    const hasChanges = changeSet.addedItems.length + changeSet.updatedItems.length + changeSet.deletedItems.length > 0
    if (hasChanges && autoSave && focusedView !== 'receipt' && jobItemsQuery.data) {
      handleDebounce(jobId, changeSet, jobItemsQuery.data)
    }
  }, [changeSet, autoSave, focusedView, jobItemsQuery.data, jobId, handleDebounce])

  const previewRequests = buildPricingPreviewRequests(jobData.workareas, jobItemsQuery.data?.estimatedItems ?? [], changeSet)
  const previews = useWorkareaPricingPreviews(jobId, jobItemsQuery.data ? previewRequests : [])
  const pricedWorkareas = applyWorkareaPricingPreviews(jobData.workareas, previewRequests.map((request, index) => ({
    workareaId: request.workareaId,
    pricing: previews[index]?.data,
  })))
  const visitPricingQuery = useVisitPricingSummaries(jobId, (visitsQuery.data ?? []).map(x => x.id),
    jobData.workareas.some(x => !!x.pricing) && (focusedView === 'visits' || focusedView === 'invoicing'))
  const pricingSignature = JSON.stringify({ workareas: pricedWorkareas, visits: visitPricingQuery.data })
  const isPricingPending = previews.some(x => x.isLoading || x.isFetching)
  const isPricingError = !isPricingPending && previews.some(x => x.isError)

  useEffect(() => {
    const visitSummary = buildVisitSummary(visitsQuery.data ?? [], jobItemsQuery.data?.actualItems ?? [], changeSet, visitPricingQuery.data?.visits)
    const workareaSummaries = buildWorkareaSummaries(pricedWorkareas, jobItemsQuery.data?.estimatedItems ?? [], jobItemsQuery.data?.actualItems ?? [], changeSet,
      completedWorkareaVisitsQuery.data ?? [], visitPricingQuery.data?.nonVisit)
    setWorkareaSummaries(workareaSummaries)
    setVisitSummary(visitSummary)
    onChange(changeSet, visitSummary, workareaSummaries)
  }, [jobItemsQuery.data, changeSet, visitsQuery.data, pricingSignature, completedWorkareaVisitsQuery.data, triggerSummariesCounter])

  useEffect(() => {
    if (changeSet.errorMessage) {
      notificationApi.error({ message: changeSet.errorMessage })
      dispatch({ type: 'CLEAR_ERROR_MESSAGE' })
    }
  }, [changeSet.errorMessage])

  const [lastAddedCatalogItem, setLastAddedCatalogItem] = useState<LastAddedCatalogItemWithName | undefined>()

  const jobItemsInvalidator = useJobItemsInvalidator()
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  const visitsInvalidator = useInvalidateVisits()
  const pricingInvalidator = useWorkareaPricingInvalidator()

  useEffect(() => {
    dispatch({ type: 'CLEAR_CHANGE_SET' })
    jobItemsInvalidator.invalidateForJob(jobId)
    workareaGroupsInvalidator.invalidateForJob(jobId)
    visitsInvalidator(jobId)
    pricingInvalidator.invalidateForJob(jobId)
  }, [saveCounter])

  const dispatchToProvide = getRecurringDispatchIfNeeded(dispatch, focusedView, jobData.workareas, jobItemsQuery.data?.estimatedItems ?? [], changeSet.addedItems, focusedWorkareaId, copyChangesToChildWorkareas)

  return <ItemsContext.Provider value={{ changeSet, dispatch: dispatchToProvide, jobData, focusedView, focusedWorkareaId, focusedReceiptId, jobItemsQuery, lastAddedCatalogItem, workareaSummaries, autoSave: autoSave ?? false, isPricingPending, isPricingError, visitSummary }}>
    {notificationContext}
    {isPricingError && <Alert type="error" message="Unable to calculate work area pricing." action={<Button onClick={() => previews.filter(x => x.isError).forEach(x => x.refetch())}>Retry</Button>} />}
    {visitPricingQuery.isError && <Alert type="error" message="Unable to calculate visit pricing." action={<Button onClick={() => visitPricingQuery.refetch()}>Retry</Button>} />}
    <CatalogItemHandler onTemplateAdd={onTemplateAdd} setLastAddedCatalogItem={setLastAddedCatalogItem} />
    {children}
  </ItemsContext.Provider>
}

export default ItemsRoot
